import { adminManageUserJoinPageApi } from 'api/Admin/adminManageUserJoinPageApi';
import useToggle from 'hooks/CustomHooks/useToggle';
import React, { useEffect, useState } from 'react';
import { useLoginStore } from 'store/useLoginDataStore';
import { BorderColor, Accent, TextColor } from 'styles/Global/colorSemantic';
import { PageTitle, RowDivider } from 'styles/Global/commonStyles';
import { DiagnosisLayout, FilterWrapper, HospitalPageLabel } from 'styles/Global/hospitalStyles';
import { useLoadingStore } from 'store/useLoadingStore';
import { AdminInnerLayout } from 'styles/Global/Admin/adminCommonStyles';
import { AccountLevel } from 'utils/statusCode';
import AdminCounselerListPagination from './AdminCounselerListPagination';
import { AdminCounselerListConstants } from 'constants/Admin/AdminCouncelerListPageConstants';
import { CounselorScheduleResponse, UpdateCounselerScheduleRequest } from 'models/Admin/AdminPaginationTypes';
import CounselerScheduleModal from 'components/Admin/Modal/CounselerScheduleModal';
import { CounselReserveCalendar } from 'components/Common/Calendar';
import styled from 'styled-components';
import BasicButton from 'components/Buttons/BasicButton';
import useAvailableSlots from 'hooks/CustomHooks/useAvailableSlots';
import _ from 'lodash';
import moment from 'moment';
import 'react-calendar/dist/Calendar.css';
import 'styles/Global/customCalender.css';
import { mindUpHolidayApi } from 'api/MindUp/mindUpHolidayApi';
import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';
import { GetUserListApiListType } from 'models/DCKAdmin/AdminManageUserJoinPageTypes';

interface ModalProps {
    title: string;
    onClose: () => void;
    children: React.ReactNode;
}

const ScheduleCalendarModal = ({ title, onClose, children }: ModalProps) => {
    return (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                </ModalHeader>
                {children}
                <ButtonContainer>
                    <BasicButton $type='fill' $width='120px' onClick={onClose}>
                        닫기
                    </BasicButton>
                </ButtonContainer>
            </ModalContainer>
        </ModalOverlay>
    );
};

const AdminCounselerListPage = () => {
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
    const [selectedList, setSelectedList] = useState<SelectDateType[]>([{ id: 0, date: '' }]);
    const [userData, setUserData] = useState<GetUserListApiListType[]>();
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedUserData, setSelectedUserData] = useState<GetUserListApiListType | null>(null);
    const [scheduleData, setScheduleData] = useState<CounselorScheduleResponse | null>(null);
    const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null);
    const [holidayList, setHolidayList] = useState<string[]>([]);

    // TODO: 병원 정보 하드코딩
    const hospitals = [
        {
            id: '67d14e8c564cfdb75af9a281',
            name: '테스트심리상담센터',
        },
        {
            id: '677e22fe3d73c33a066fc54a',
            name: 'TLC 심리상담센터',
        },
    ];

    const { availableSlots: slotsData, isLoading: isSlotsLoading } = useAvailableSlots(selectedHospitalId || undefined);

    const [isScheduleModal, setIsScheduleModal] = useToggle();
    const [isCalendarModal, setIsCalendarModal] = useToggle();

    const { setPage, currentPage, pageGroup, handlePageGroup } = usePaginationStateHooks(userData);

    const reserveTimeList = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'];

    useEffect(() => {
        adminManageUserJoinPageApiCall();
    }, [currentPage]);

    useEffect(() => {
        fetchHolidays();
    }, []); // 컴포넌트 마운트 시에만 실행

    const fetchHolidays = async () => {
        try {
            const response = await mindUpHolidayApi.getHoliday();
            if (response.data && response.data.result) {
                setHolidayList(response.data.result);
            }
        } catch (error) {
            console.error('Error fetching holidays:', error);
        }
    };

    // 선택 가능한 첫 날짜 계산
    const getValidDate = React.useMemo(() => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // 주말이나 공휴일인지 확인
        const isHoliday = (date: Date) => {
            const formattedDate = moment(date).format('YYYYMMDD');
            return (
                holidayList.includes(formattedDate) ||
                date.getDay() === 0 || // 일요일
                date.getDay() === 6
            ); // 토요일
        };

        let checkDate = tomorrow;
        // 다음 영업일 찾기
        while (isHoliday(checkDate)) {
            checkDate.setDate(checkDate.getDate() + 1);
        }

        return moment(checkDate).format('YYYY/MM/DD');
    }, [holidayList]);

    // selectedDate가 null일 때 기본값으로 유효한 날짜 사용
    const validSelectedDate = selectedDate || getValidDate;

    // user list api call
    const adminManageUserJoinPageApiCall = async () => {
        setLoading(true);

        try {
            const response = await adminManageUserJoinPageApi.getUserList(currentPage, null, null, loginData.token, AccountLevel.externalCounselor);

            if (response.data) {
                setUserData(response.data.list);
                setTotalPages(response.data.pagination.totalPages);
                setTotalCount(response.data.pagination.totalCount);
            } else {
                console.log(response);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    // reset Button function
    const handleResetButton = async () => {
        const response = await adminManageUserJoinPageApi.getUserList(currentPage, null, null, loginData.token, null);
        setUserData(response.data.list);
        setPage(1);
        handlePageGroup(0);
    };

    const clickScheduleManageButton = async (userData: GetUserListApiListType, type: string) => {
        try {
            const response = await adminManageUserJoinPageApi.getCounselerSchedule(userData._id, loginData.token);
            if (response.status === 'ok') {
                setSelectedUserData(userData);
                setScheduleData(response.data);
                setSelectedDate('2025/04/14');
                console.log('selectedDate', selectedDate);
                setIsScheduleModal();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleScheduleUpdate = async (day: number, timeSlot: string, isAvailable: boolean) => {
        try {
            const updateScheduleData: UpdateCounselerScheduleRequest = {
                day: day,
                timeSlot: timeSlot,
                isAvailable: isAvailable,
                userId: scheduleData!.userId,
            };
            const response = await adminManageUserJoinPageApi.updateCounselerSchedule(updateScheduleData, loginData.token);

            if (response.status === 'ok') {
                console.log(response.data);
                setScheduleData(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleDateSelect = (date: string | null) => {
        setSelectedDate(date);
        setSelectedTimeSlot(null);

        if (date) {
            const formattedDate = moment(date).format('YYYYMMDD');
            const updatedList = [...selectedList];
            updatedList[0] = { id: 0, date: formattedDate + (selectedTimeSlot?.replace(':', '') ?? '0000') };
            setSelectedList(updatedList);
        }
    };

    // 모달 열 때 첫 번째 병원 자동 선택
    const handleOpenCalendarModal = () => {
        if (hospitals.length > 0 && !selectedHospitalId) {
            setSelectedHospitalId(hospitals[0].id);
        }
        setIsCalendarModal();
    };

    return (
        <DiagnosisLayout>
            {isScheduleModal && scheduleData && <CounselerScheduleModal toggle={setIsScheduleModal} scheduleData={scheduleData} onScheduleUpdate={handleScheduleUpdate} />}
            {isCalendarModal && (
                <ScheduleCalendarModal
                    title='상담 예약 현황'
                    onClose={() => {
                        setIsCalendarModal();
                        setSelectedHospitalId(null);
                        setSelectedDate(null);
                    }}
                >
                    <ModalContent>
                        <HospitalSelectWrapper>
                            <HospitalList>
                                {hospitals.map((hospital) => (
                                    <HospitalItem
                                        key={hospital.id}
                                        $isSelected={selectedHospitalId === hospital.id}
                                        onClick={() => {
                                            setSelectedHospitalId(hospital.id);
                                            setSelectedDate(null);
                                            // 병원 선택 시 유효한 날짜로 자동 선택
                                            const validDate = getValidDate;
                                            handleDateSelect(validDate);
                                        }}
                                    >
                                        {hospital.name}
                                    </HospitalItem>
                                ))}
                            </HospitalList>
                        </HospitalSelectWrapper>

                        {selectedHospitalId && (
                            <CalendarAndTimeWrapper>
                                <CalendarSection>
                                    <CounselReserveCalendar
                                        holidayList={holidayList}
                                        selectedDate={validSelectedDate}
                                        setSelectedDate={handleDateSelect}
                                        handleSelectedCalendarList={(list) => setSelectedList(list)}
                                    />
                                </CalendarSection>
                                <TimeSlotSection>
                                    {selectedDate ? (
                                        <>
                                            <TimeSlotHeader>{moment(selectedDate).format('YYYY년 MM월 DD일')}</TimeSlotHeader>
                                            <TimeSlotGrid>
                                                {reserveTimeList.map((time) => {
                                                    const formattedDate = moment(selectedDate).format('YYYYMMDD');
                                                    const availableCount = slotsData[formattedDate]?.[time]?.availableCount ?? 0;
                                                    const isOccupied = availableCount <= 0;

                                                    return (
                                                        <TimeSlotLabel key={time} $isOccupied={isOccupied}>
                                                            <TimeText $isOccupied={isOccupied}>{moment(time, 'HH:mm').format('A h:mm')}</TimeText>
                                                            <CountText $count={availableCount}>{availableCount}</CountText>
                                                        </TimeSlotLabel>
                                                    );
                                                })}
                                            </TimeSlotGrid>
                                        </>
                                    ) : (
                                        <div style={{ textAlign: 'center', color: TextColor.color_text_weak }}>날짜를 선택해주세요</div>
                                    )}
                                </TimeSlotSection>
                            </CalendarAndTimeWrapper>
                        )}
                    </ModalContent>
                </ScheduleCalendarModal>
            )}
            <AdminInnerLayout>
                <HospitalPageLabel onClick={() => window.location.reload()}>{AdminCounselerListConstants.pageCommonConstants.title}</HospitalPageLabel>
                <PageTitle $marginbottom='24px'>{AdminCounselerListConstants.pageCommonConstants.title}</PageTitle>
                <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                <FilterWrapper>
                    총 {totalCount || 0} 명
                    <BasicButton $type='outLine' $width='120px' onClick={handleOpenCalendarModal}>
                        상담 예약 현황
                    </BasicButton>
                </FilterWrapper>
                <AdminCounselerListPagination
                    initNumber={userData?.length}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageGroup={pageGroup}
                    handlePageGroup={handlePageGroup}
                    handlePageChange={setPage}
                    currentData={userData}
                    totalCount={totalCount}
                    clickScheduleManageButton={clickScheduleManageButton}
                    handleResetButton={handleResetButton}
                />
            </AdminInnerLayout>
        </DiagnosisLayout>
    );
};

export default AdminCounselerListPage;

const TimeSlotGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    width: 100%;
`;

const TimeSlotLabel = styled.div<{ $isOccupied: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    border-radius: 8px;
    background-color: ${(props) => (props.$isOccupied ? '#f5f5f5' : '#ffffff')};
    border: 1px solid ${(props) => (props.$isOccupied ? '#e0e0e0' : '#d1d1d1')};
    min-width: 80px;
`;

const TimeText = styled.span<{ $isOccupied: boolean }>`
    font-size: 13px;
    color: ${(props) => (props.$isOccupied ? '#999999' : '#333333')};
    margin-bottom: 2px;
`;

const CountText = styled.span<{ $count: number }>`
    font-size: 11px;
    color: ${(props) => (props.$count > 0 ? '#4CAF50' : '#f44336')};
    font-weight: 500;
`;

interface SelectDateType {
    id: number;
    date: string;
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContainer = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    min-width: 800px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
`;

const ModalHeader = styled.div`
    text-align: center;
    margin-bottom: 32px;
    padding: 0 48px;
`;

const ModalTitle = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 32px;
    width: 100%;
`;

const CalendarSection = styled.div`
    flex: 0 0 auto;
    width: 280px;
`;

const TimeSlotSection = styled.div`
    flex: 1;
    padding: 0 16px;
    min-width: 300px;
`;

const TimeSlotHeader = styled.div`
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 16px;
    color: #333;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const HospitalSelectWrapper = styled.div`
    width: 100%;
`;

const HospitalList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

const HospitalItem = styled.div<{ $isSelected: boolean }>`
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid ${(props) => (props.$isSelected ? Accent.color_accent_lime : BorderColor.color_border_neutral)};
    background-color: ${(props) => (props.$isSelected ? Accent['color-accent-lime-opacity-8'] : '#ffffff')};
    cursor: pointer;
    font-size: 14px;

    &:hover {
        border-color: ${Accent.color_accent_lime};
    }
`;

const CalendarAndTimeWrapper = styled.div`
    display: flex;
    gap: 32px;
    width: 100%;
`;
